import { useEffect } from 'react';

import { useClaim } from '~/components/ClaimContainer';
import { useCms } from '~/components/hooks/useCms';
import useDataFetcher from '~/components/useDataFetcher';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import type { ClaimPolymorph, LegacyClaimPolymorph } from '../types/claim-types';
import type { ExposureModel, ExposureTotalsModel } from '../types/exposure-types';

import type { ExposuresAndTotalAmountsData } from './ExposureActions';
import { getExposuresUrl } from './ExposureActions';

type UseFetchExposuresReturn = {
  isExposuresLoading: boolean;
  isExposuresError: boolean;
  exposures: ClaimPolymorph['exposures'];
  exposuresTotals?: ExposureTotalsModel;
  reloadData: () => Promise<
    | {
        exposures: ExposureModel[];
        exposures_totals?: ExposureTotalsModel;
      }
    | undefined
  >;
};

interface UseFetchExposuresOptions {
  shouldFetch?: boolean;
}

type LegacyClaimNetPaidTotals = {
  total_indemnity_net_paid_including_pending_minus_recoveries?: number;
  total_expenses_net_paid_including_pending_minus_recoveries?: number;
};

export default function useFetchExposures({
  shouldFetch = true,
}: UseFetchExposuresOptions = {}): UseFetchExposuresReturn {
  const { userOrganization } = useCms();
  const { claim, reloadingUuid } = useClaim({ claimRequired: true });
  const isEfficientClaimForFe = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL);
  const {
    isLoading: isExposuresLoading,
    isError: isExposuresError,
    data,
    reloadData,
  } = useDataFetcher<ExposuresAndTotalAmountsData>(
    isEfficientClaimForFe && claim.id ? getExposuresUrl(claim.id) : null,
    undefined,
    shouldFetch
  );

  // on update claim - update the exposures
  useEffect(() => {
    if (!isExposuresLoading && reloadingUuid) {
      reloadData();
    }
  }, [isExposuresLoading, reloadData, reloadingUuid]);

  if (!isEfficientClaimForFe) {
    // The claim schema serves the net (excl. VAT/GST) paid totals alongside the gross ones, but the
    // generated claim contract has not been regenerated with them yet, so they are read through this
    // narrow local shape rather than by widening the generated type.
    const legacyClaim = claim as LegacyClaimPolymorph & LegacyClaimNetPaidTotals;
    return {
      isExposuresLoading: false,
      isExposuresError: false,
      exposures: claim.exposures,
      exposuresTotals: {
        total_indemnity_reserve_including_pending: legacyClaim.total_indemnity_reserve_including_pending || 0,
        total_expenses_reserve_including_pending: legacyClaim.total_expenses_reserve_including_pending || 0,
        total_indemnity_paid_including_pending_minus_recoveries:
          legacyClaim.total_indemnity_paid_including_pending_minus_recoveries || 0,
        total_expenses_paid_including_pending_minus_recoveries:
          legacyClaim.total_expenses_paid_including_pending_minus_recoveries || 0,
        total_indemnity_net_paid_including_pending_minus_recoveries:
          legacyClaim.total_indemnity_net_paid_including_pending_minus_recoveries || 0,
        total_expenses_net_paid_including_pending_minus_recoveries:
          legacyClaim.total_expenses_net_paid_including_pending_minus_recoveries || 0,
        total_deductible_amount: legacyClaim.total_deductible_amount || 0,
      },
      reloadData: () => Promise.resolve({ exposures: [] }),
    };
  }

  if (isExposuresLoading) {
    return {
      isExposuresLoading,
      isExposuresError,
      exposures: [],
      reloadData: () => Promise.resolve({ exposures: [] }),
    };
  }

  const result = { exposures: [], ...data };
  return {
    isExposuresLoading,
    isExposuresError,
    exposures: result.exposures,
    exposuresTotals: result.exposures_totals,
    reloadData,
  };
}
